import { useId, type ReactNode } from 'react';

import Link from '@eliadmin/core/src/components/link';
import { Icon, type IconName } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Arrow-key stops of the panel (rows, chips, buttons). The arrows move the focus between them in
 * DOM order, the field comes before the first one; remove buttons and add to cart are Tab stops only.
 */
export const OPTION = { 'data-search-option': '' } as const;
export const OPTION_SELECTOR = '[data-search-option]';

export const FOCUS = 'focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Figma 12 px styles of the panel are 120 % high: Body/S Semibold (group labels) and Body/Caption
 * (details) – the nearest classes with the Figma line height.
 */
export const LABEL = 'nt-caption-semibold leading-[1.2] text-[#5D655D]';
export const CAPTION = 'nt-caption leading-[1.2] text-[#5D655D]';

/**
 * Grid tracks of the desktop panel in the Figma column widths (of 1328) – only for the columns that
 * have content, the others share their width.
 */
export const columnTracks = (widths: number[]) =>
  widths.map(width => `minmax(0, ${width}fr)`).join(' ');

/** Lower case without diacritics (combining marks), so „tlaciaren“ finds „tlačiareň“. */
const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Figma result name: the typed words in bold where a word starts with them, one run when they
 * follow each other („HP 85“A (CE285A) – not the 85 inside the code). Matches on the folded text
 * and maps every match back to the original characters – the core `Highlight` escapes the name
 * itself, so names with brackets („[TEST] … (CE285A)“) shift its bold.
 */
export function MatchText({ text, query }: { text: string; query: string }) {
  const terms = fold(query).split(/\s+/).filter(Boolean).map(escapeRegExp);
  if (!terms.length) return text;

  // origin[i] = index in `text` of the character the i-th folded code unit comes from (+ the end).
  let folded = '';
  const origin: number[] = [];
  Array.from(text).reduce((index, char) => {
    const part = fold(char);
    folded += part;
    for (let unit = 0; unit < part.length; unit++) origin.push(index);
    return index + char.length;
  }, 0);
  origin.push(text.length);

  // Group 1 = the start of the text or the character before the word (no lookbehind – older Safari).
  const term = `(?:${terms.join('|')})`;
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])(${term}(?:\\s+${term})*)`, 'gu');
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of folded.matchAll(pattern)) {
    const from = match.index + match[1].length;
    const start = origin[from];
    const end = origin[from + match[2].length];
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <mark key={start} className="bg-transparent font-bold text-inherit">
        {text.slice(start, end)}
      </mark>,
    );
    last = end;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Figma „Chip“ (40 px, radius 16, line border, Body/M Semibold) – as the menu panel chips. */
const CHIP = cn(
  'nt-body-m-semibold flex h-10 items-center gap-1.5 rounded-2xl border border-[#E2E5DF] px-3 text-[#0D110D] transition-colors hover:border-[#CDD2CA]',
  FOCUS,
);

/**
 * Titled block of the panel („Tlačiarne“, „Populárne vyhľadávania“ …): Body/S Semibold #5D655D,
 * 8 px above its content; `action` sits at the end of the label row („Vymazať“).
 */
export function SearchGroup({
  title,
  action,
  className,
  children,
}: {
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();

  return (
    <div
      role="group"
      aria-labelledby={title ? id : undefined}
      className={cn('flex min-w-0 flex-col gap-2', className)}
    >
      {title || action ? (
        <div className="flex items-center gap-2">
          <p id={id} className={cn(LABEL, 'min-w-0 flex-1')}>
            {title}
          </p>
          {action}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** Chips of a group, wrapped 8 px apart. */
export function SearchChips({
  chips,
  icon,
  onSelect,
}: {
  chips: { key: string | number; label: string; href: string }[];
  icon?: IconName;
  onSelect: (label: string) => void;
}) {
  return (
    <ul role="list" className="flex flex-wrap gap-2">
      {chips.map(chip => (
        <li key={chip.key}>
          <Link {...OPTION} href={chip.href} onClick={() => onSelect(chip.label)} className={CHIP}>
            {icon ? <Icon name={icon} size={14} className="text-[#127A26]" /> : null}
            {chip.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Figma „Row“ of „Top kategórie“, „Tlačiarne“, „Poradňa“: green icon, Body/L Bold title (Body/Caption
 * detail under it), the #8F978E caret at the end; 8 px above and below.
 */
export function SearchLinkRow({
  href,
  icon,
  title,
  detail,
  onClick,
}: {
  href: string;
  icon: ReactNode;
  title: string;
  detail?: string;
  onClick: () => void;
}) {
  return (
    <Link
      {...OPTION}
      href={href}
      onClick={onClick}
      className={cn('group flex items-center gap-3 rounded-[8px] py-2', FOCUS)}
    >
      {icon}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="nt-body-l-bold text-[#0D110D] transition-colors group-hover:text-[#0E6A20]">
          {title}
        </span>
        {detail ? <span className={CAPTION}>{detail}</span> : null}
      </span>
      <Icon name="caret-right" size={16} className="text-[#8F978E]" />
    </Link>
  );
}
